import { mountMap } from "./mount";
import type { EventMapSDK, ErrorDetail } from "./types";
import { EmbedError } from "./validation";

export type {
  EmbedOptions,
  EmbedHandle,
  EventMapSDK,
  ReadyDetail,
  SelectDetail,
  ErrorDetail,
  ViewChangeDetail,
  RouteChangeDetail,
} from "./types";
export type {
  MapViewState,
  MapRouteChange,
  MapRouteStatus,
} from "../shared/mapControls";
export type {
  MapDiagnostic,
  MapDiagnosticCode,
  MapDiagnosticCallback,
} from "./diagnostics";
export type { MapRouteStyle } from "../shared/types";

const currentScript =
  document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : null;
const serviceBase = currentScript?.src
  ? new URL(currentScript.src).origin
  : window.location.origin;
const sdk: EventMapSDK = {
  version: "1",
  mount: (target, options) =>
    mountMap(
      target,
      { ...options, nonce: options?.nonce ?? currentScript?.nonce },
      serviceBase,
    ),
};
window.GSMAEventMap = sdk;

function autoMount(script: HTMLScriptElement): void {
  const mapId = script.dataset.map;
  if (!mapId || script.dataset.eventMapMounted === "true") return;
  script.dataset.eventMapMounted = "true";
  let target: HTMLElement | string;
  if (script.dataset.target) target = script.dataset.target;
  else {
    const container = document.createElement("div");
    container.dataset.eventMapContainer = mapId;
    if (script.closest("body"))
      script.parentNode?.insertBefore(container, script.nextSibling);
    else document.body.appendChild(container);
    target = container;
  }
  const defaultBase = script.src ? new URL(script.src).origin : serviceBase;
  void mountMap(
    target,
    {
      mapId,
      apiBase: script.dataset.apiBase,
      nonce: script.nonce,
      controls: script.dataset.controls !== "false",
    },
    defaultBase,
  ).catch((cause: unknown) => {
    const error =
      cause instanceof EmbedError
        ? cause
        : new EmbedError(
            "invalid_target",
            "The map could not be attached to the requested element.",
          );
    script.dispatchEvent(
      new CustomEvent<ErrorDetail>("event-map:error", {
        detail: { mapId, code: error.code, message: error.message },
        bubbles: true,
        composed: true,
      }),
    );
  });
}

if (currentScript?.dataset.map) {
  if (document.readyState === "loading")
    document.addEventListener(
      "DOMContentLoaded",
      () => autoMount(currentScript),
      { once: true },
    );
  else autoMount(currentScript);
}
